<template>
  <controller>
    <main class="sgu-report-page">
      <header class="report-header">
        <div class="report-heading">
          <div class="report-title">
            <button type="button" class="header-back-action" @click="goBack">&larr; Voltar</button>
            <span class="header-eyebrow">SGU · Camadas de Performance</span>
            <h1>Relatório de Desempenho</h1>
            <p v-if="report">{{ unitLabel }} · Semestre {{ report.cycle.code }}</p>
            <p v-else>Visão interativa das respostas e critérios avaliados.</p>
          </div>
          <div class="report-header-actions">
            <label v-if="report && !isSharedReport" class="semester-selector">
              <span>Semestre</span>
              <select v-model.number="cycleId" :disabled="loading || printLoading" @change="changeCycle">
                <option v-for="cycle in (report.cycles || [report.cycle])" :key="cycle.id" :value="cycle.id">{{ cycle.code }}</option>
              </select>
            </label>
            <button v-if="report" type="button" class="header-action primary" :disabled="loading || printLoading" @click="printReport">
              {{ printLoading ? 'Gerando PDF...' : 'Imprimir relatório' }}
            </button>
            <span v-if="report" :class="['status-badge', { complete: report.summary.status === 'Completo' }]">
              {{ report.summary.status }}
            </span>
          </div>
        </div>
        <p v-if="printError" class="header-error" role="alert">{{ printError }}</p>
      </header>

      <section v-if="loading && !report" class="report-skeleton" aria-live="polite" aria-label="Carregando relatório">
        <span>Carregando relatório do SGU...</span>
        <div class="skeleton-heading" />
        <div class="skeleton-grid">
          <i v-for="item in 4" :key="item" />
        </div>
        <div class="skeleton-content" />
      </section>

      <section v-else-if="errorMessage" class="state-panel error" role="alert">
        <strong>Não foi possível carregar o relatório</strong>
        <p>{{ errorMessage }}</p>
        <button type="button" @click="loadReport">Tentar novamente</button>
      </section>

      <template v-else-if="report">
        <section v-if="report.summary.includesDrafts" class="preview-notice" role="status" aria-live="polite">
          <strong>Prévia com rascunhos</strong>
          <p>{{ previewDraftMessage }}</p>
        </section>

        <section id="visao-geral" class="summary-section" aria-labelledby="summary-title">
          <div class="section-heading">
            <div>
              <span class="eyebrow">Resultado do ciclo</span>
              <h2 id="summary-title">Visão geral</h2>
            </div>
            <small>Consultado em {{ report.generatedAt }}</small>
          </div>

          <div class="summary-grid" aria-live="polite">
            <article :class="['primary-summary', summaryLayerClass]">
              <span>Desempenho geral</span>
              <strong>{{ formatPercentage(report.summary.score) }}</strong>
              <small>{{ report.summary.layer }}</small>
            </article>
            <article>
              <span>{{ report.summary.includesDrafts ? 'Requisitos considerados' : 'Requisitos avaliados' }}</span>
              <strong>{{ consideredRequirements }}/{{ report.summary.total }}</strong>
              <small v-if="report.summary.includesDrafts">
                {{ report.summary.submitted }} enviados · {{ report.summary.drafts }} em rascunho
              </small>
              <small v-else>{{ coveragePercentage }}% de cobertura</small>
            </article>
            <article>
              <span>Média da rede</span>
              <strong>{{ formatPercentage(report.summary.networkAverage) }}</strong>
              <small>{{ report.summary.networkComplete ? 'Referência do ciclo' : 'Disponível após todas as unidades serem avaliadas' }}</small>
            </article>
            <article>
              <span>Posição na rede</span>
              <strong>{{ rankingLabel }}</strong>
              <small v-if="report.summary.networkComplete && report.summary.rankingTotal">
                {{ report.summary.rankingTotal }} unidades avaliadas{{ report.summary.includesDrafts ? ' · projeção' : '' }}
              </small>
              <small v-else>{{ report.summary.evaluatedUnits || 0 }} de {{ report.summary.expectedUnits || report.summary.rankingTotal || 0 }} unidades concluídas</small>
            </article>
          </div>
        </section>

        <section class="content-panel calculation-panel" aria-labelledby="calculation-title">
          <div class="section-heading">
            <div>
              <span class="eyebrow">Critérios e pesos</span>
              <h2 id="calculation-title">Como a nota é calculada</h2>
            </div>
            <button
              type="button"
              class="calculation-toggle"
              :aria-expanded="showCalculation"
              aria-controls="calculation-content"
              @click="showCalculation = !showCalculation"
            >
              {{ showCalculation ? 'Ocultar método' : 'Ver método de cálculo' }}
            </button>
          </div>

          <div v-if="showCalculation" id="calculation-content" class="calculation-content">
            <p>Cada requisito é pontuado de acordo com a camada atingida. Na nota final, cada requisito tem um peso diferente, que define seu impacto na nota final. O resultado é a média ponderada dessas pontuações, expressa em percentual.</p>
            <div class="calculation-grid">
              <div class="score-scale" aria-label="Pontuação por camada">
                <span><i class="layer-not-applicable" aria-hidden="true" /><strong>Não se aplica</strong><b>Não pontua</b></span>
                <span><i class="layer-not-met" aria-hidden="true" /><strong>Não Atende</strong><b>0 pontos</b></span>
                <span><i class="layer-basic" aria-hidden="true" /><strong>Básico</strong><b>1 ponto</b></span>
                <span><i class="layer-sustainable" aria-hidden="true" /><strong>Sustentável</strong><b>2 pontos</b></span>
                <span><i class="layer-high" aria-hidden="true" /><strong>Alta Performance</strong><b>3 pontos</b></span>
              </div>
              <div class="classification-box">
                <p>Os intervalos de classificação são:</p>
                <ul>
                  <li><span>Até 49%</span><strong>Básico</strong></li>
                  <li><span>50% a 79%</span><strong>Sustentável</strong></li>
                  <li><span>80% ou mais</span><strong>Alta Performance</strong></li>
                </ul>
              </div>
            </div>
            <ul class="weight-explanation">
              <li><strong>Peso 1</strong><span>Requisitos objetivos e de rápida adequação.</span></li>
              <li><strong>Peso 2</strong><span>Dependem de fluxos, treinamento e constância operacional.</span></li>
              <li><strong>Peso 3</strong><span>Dependem de cultura, liderança e mudança de comportamento.</span></li>
            </ul>
          </div>
        </section>

        <section class="content-panel report-filter-panel" aria-labelledby="report-filters-title">
          <div class="section-heading">
            <div>
              <span class="eyebrow">Filtros do relatório</span>
              <h2 id="report-filters-title">Refinar resultados</h2>
            </div>
            <button type="button" class="clear-filter-action" @click="clearFilters">Limpar filtros</button>
          </div>
          <div class="report-filter-grid">
            <label><span>Área responsável</span><select v-model="selectedArea"><option value="">Todas</option><option v-for="area in areaOptions" :key="area.id" :value="String(area.id)">{{ area.name }}</option></select></label>
            <label><span>Camada</span><select v-model="selectedLayer"><option v-for="layer in layerOptions" :key="layer.value" :value="layer.value">{{ layer.label }}</option></select></label>
            <label><span>Área da mandala</span><select v-model="selectedMandalaArea"><option value="">Todas</option><option v-for="area in mandalaAreaOptions" :key="area.id || area.name" :value="String(area.id || area.name)">{{ area.name }}</option></select></label>
          </div>
        </section>

        <section class="distribution-grid" aria-label="Distribuições das respostas do CSC">
          <article class="content-panel distribution-card distribution-panel" aria-labelledby="distribution-title">
            <div class="section-heading">
              <div>
                <span class="eyebrow">Código de cores por camada</span>
                <h2 id="distribution-title">Distribuição por camada</h2>
              </div>
              <small>{{ distributionTotal }} respostas do CSC</small>
            </div>

            <template v-if="distributionTotal">
              <div class="performance-chart-legend" aria-label="Legenda das camadas">
                <span v-for="row in distributionChartRows" :key="row.value">
                  <i :style="{ backgroundColor: row.color }" aria-hidden="true" />
                  {{ row.label }} ({{ row.count }})
                </span>
              </div>
              <div class="performance-chart" role="img" :aria-label="distributionChartDescription">
                <Bar :data="distributionChartData" :options="distributionChartOptions" aria-hidden="true" />
              </div>
            </template>
            <div v-else class="empty-state compact">
              <strong>A distribuição ainda não está disponível</strong>
              <p>Nenhuma resposta do CSC foi registrada neste semestre.</p>
            </div>
          </article>

          <article class="content-panel distribution-card mandala-panel" aria-labelledby="mandala-distribution-title">
            <div class="section-heading">
              <div>
                <span class="eyebrow">Nota por tema · camadas de performance</span>
                <h2 id="mandala-distribution-title">Distribuição por área da mandala</h2>
              </div>
              <small>{{ mandalaAreaLabel }}</small>
            </div>

            <template v-if="mandalaAreaRows.length">
              <div class="performance-chart-legend" aria-label="Legenda das camadas">
                <span v-for="layer in mandalaChartLayers" :key="layer.value">
                  <i :style="{ backgroundColor: layer.color }" aria-hidden="true" />
                  {{ layer.label }} ({{ mandalaLayerTotals[layer.value] }})
                </span>
              </div>
              <div class="performance-chart" role="img" :aria-label="mandalaChartDescription">
                <Bar :data="mandalaChartData" :options="mandalaChartOptions" aria-hidden="true" />
              </div>
            </template>
            <div v-else class="empty-state compact">
              <strong>A distribuição por área ainda não está disponível</strong>
              <p>Nenhuma resposta do CSC foi registrada neste semestre.</p>
            </div>
          </article>
        </section>

        <section id="divergencias" class="content-panel" aria-labelledby="divergence-title">
          <div class="section-heading">
            <div>
              <span class="eyebrow">CSC × Diretor de Unidade</span>
              <h2 id="divergence-title">Pontos de divergência</h2>
            </div>
            <small>{{ divergenceLabel }}</small>
          </div>

          <ul v-if="filteredDivergences.length" class="divergence-list">
            <li v-for="item in filteredDivergences" :key="item.id">
              <details>
                <summary>
                  <div class="divergence-copy">
                <small>{{ item.area }} · {{ item.code }}</small>
                <strong>{{ item.name }}</strong>
                <span v-if="item.cscStatus === 'draft' || item.directorStatus === 'draft'" class="draft-marker">
                  {{ draftSourceLabel(item) }}
                </span>
                  </div>
                  <div class="comparison">
                <span><small>CSC</small><b :class="['layer-badge', layerClass(item.cscLayer)]">{{ item.cscLayerLabel }}</b></span>
                <span aria-hidden="true" class="comparison-arrow">&rarr;</span>
                <span><small>Diretor</small><b :class="['layer-badge', layerClass(item.directorLayer)]">{{ item.directorLayerLabel }}</b></span>
                  </div>
                  <strong class="difference">{{ differenceLabel(item.difference) }}</strong>
                </summary>
                <div class="divergence-details">
                  <div class="criterion-grid">
                    <article><span class="criterion-label">Básico</span><p>{{ item.basicDescription || 'Critério não informado.' }}</p></article>
                    <article><span class="criterion-label">Sustentável</span><p>{{ item.sustainableDescription || 'Critério não informado.' }}</p></article>
                    <article><span class="criterion-label">Alta Performance</span><p>{{ item.highPerformanceDescription || 'Critério não informado.' }}</p></article>
                  </div>
                  <div class="answer-information">
                    <div v-if="item.justification"><span>Justificativa do CSC</span><p>{{ item.justification }}</p></div>
                    <div v-if="item.evidenceLink"><span>Evidência</span><a v-if="safeEvidenceUrl(item.evidenceLink)" :href="safeEvidenceUrl(item.evidenceLink)" target="_blank" rel="noopener noreferrer">Abrir evidência</a><p v-else>{{ item.evidenceLink }}</p></div>
                  </div>
                </div>
              </details>
            </li>
          </ul>
          <div v-else class="empty-state">
            <strong>Nenhuma divergência encontrada</strong>
            <p>As respostas do CSC e do Diretor estão na mesma camada nos requisitos comparáveis.</p>
          </div>
        </section>

        <section id="requisitos" class="content-panel requirements-panel" aria-labelledby="requirements-title">
          <div class="section-heading">
            <div>
              <span class="eyebrow">Detalhamento</span>
              <h2 id="requirements-title">Desempenho por requisito</h2>
            </div>
            <small>{{ filteredRequirements.length }} de {{ report.details.length }} requisitos</small>
          </div>

          <div class="requirement-filters">
            <label class="search-field">
              <span>Buscar requisito</span>
              <input v-model.trim="search" type="search" placeholder="Nome, código ou área">
            </label>
            <fieldset>
              <legend>Filtrar pela camada do CSC</legend>
              <div class="layer-filters">
                <button
                  v-for="layer in layerOptions"
                  :key="layer.value"
                  type="button"
                  :class="{ active: selectedLayer === layer.value }"
                  :aria-pressed="selectedLayer === layer.value"
                  @click="selectedLayer = layer.value"
                >
                  <i v-if="layer.value" :class="layerClass(layer.value)" aria-hidden="true" />
                  {{ layer.label }}
                  <span>{{ layerCount(layer.value) }}</span>
                </button>
              </div>
            </fieldset>
          </div>

          <div v-if="filteredRequirements.length" class="requirements-list">
            <details v-for="item in filteredRequirements" :key="item.id">
              <summary>
                <div class="requirement-title">
                  <small>{{ item.area }} · {{ item.code }}</small>
                  <strong>{{ item.name }}</strong>
                  <span v-if="item.cscStatus === 'draft' || item.directorStatus === 'draft'" class="draft-marker">
                    {{ draftSourceLabel(item) }}
                  </span>
                </div>
                <div class="requirement-result">
                  <span class="weight">Peso {{ formatWeight(item.weight) }}</span>
                  <span :class="['layer-badge', layerClass(item.cscLayer)]">{{ item.cscLayerLabel }}</span>
                  <strong>{{ scoreLabel(item.cscLayer) }}</strong>
                </div>
              </summary>

              <div class="requirement-details">
                <div class="performance-track" aria-hidden="true">
                  <span :class="layerClass(item.cscLayer)" :style="{ width: scoreWidth(item.cscLayer) }" />
                </div>

                <div class="criterion-grid">
                  <article :class="{ 'selected-basic': item.cscLayer === 'basic' }">
                    <span class="criterion-label">Básico</span>
                    <p>{{ item.basicDescription || 'Critério não informado.' }}</p>
                  </article>
                  <article :class="{ 'selected-sustainable': item.cscLayer === 'sustainable' }">
                    <span class="criterion-label">Sustentável</span>
                    <p>{{ item.sustainableDescription || 'Critério não informado.' }}</p>
                  </article>
                  <article :class="{ 'selected-high': item.cscLayer === 'high_performance' }">
                    <span class="criterion-label">Alta Performance</span>
                    <p>{{ item.highPerformanceDescription || 'Critério não informado.' }}</p>
                  </article>
                </div>

                <div class="answer-information">
                  <div>
                    <span>Comparativo das respostas</span>
                    <p>
                      CSC: <strong>{{ item.cscLayerLabel }}</strong>
                      <span aria-hidden="true"> · </span>
                      Diretor: <strong>{{ item.directorLayerLabel }}</strong>
                    </p>
                  </div>
                  <div v-if="item.justification">
                    <span>Justificativa do CSC</span>
                    <p>{{ item.justification }}</p>
                  </div>
                  <div v-if="item.evidenceLink" class="evidence-information">
                    <span>Evidência</span>
                    <a
                      v-if="safeEvidenceUrl(item.evidenceLink)"
                      :href="safeEvidenceUrl(item.evidenceLink)"
                      target="_blank"
                      rel="noopener noreferrer"
                    ><b aria-hidden="true">↗</b> Abrir evidência em nova aba</a>
                    <p v-else>{{ item.evidenceLink }}</p>
                  </div>
                </div>
              </div>
            </details>
          </div>

          <div v-else class="empty-state">
            <strong>Nenhum requisito encontrado</strong>
            <p>Ajuste a busca ou selecione outra camada.</p>
            <button type="button" @click="clearFilters">Limpar filtros</button>
          </div>
        </section>
      </template>
    </main>
  </controller>
</template>

<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Bar } from 'vue-chartjs'
import { BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Tooltip } from 'chart.js'
import ImportedModulesService from '../services/importedModules.service'

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend)

const route = useRoute()
const router = useRouter()
const report = ref(null)
const loading = ref(true)
const cycleId = ref(route.query.cycleId ? Number(route.query.cycleId) : '')
const errorMessage = ref('')
const printError = ref('')
const printLoading = ref(false)
const selectedLayer = ref('')
const selectedArea = ref('')
const selectedMandalaArea = ref('')
const search = ref('')
const showCalculation = ref(false)

const layerOptions = [
  { value: '', label: 'Todas' },
  { value: 'high_performance', label: 'Alta Performance' },
  { value: 'sustainable', label: 'Sustentável' },
  { value: 'basic', label: 'Básico' },
  { value: 'not_met', label: 'Não Atende' },
  { value: 'not_applicable', label: 'Não se aplica' },
  { value: 'unanswered', label: 'Não avaliado' }
]
const chartLayers = [
  { value: 'high_performance', label: 'Alta Performance', color: '#245b92' },
  { value: 'sustainable', label: 'Sustentável', color: '#246a3a' },
  { value: 'basic', label: 'Básico', color: '#9b6300' },
  { value: 'not_met', label: 'Não Atende', color: '#a92d3b' },
  { value: 'not_applicable', label: 'Não se aplica', color: '#7b8088' }
]

const reportIdentifier = computed(() => String(route.params.unitId || ''))
const isSharedReport = computed(() => isUuid(reportIdentifier.value))
const isAdministrativePreview = computed(() => /^\d+$/.test(reportIdentifier.value))
const unitLabel = computed(() => {
  const code = report.value?.unit.code || ''
  const name = report.value?.unit.name || ''
  if (!code) return name
  return normalizeSearch(code) === normalizeSearch(name) || !name ? code : `${code} · ${name}`
})
const summaryLayerClass = computed(() => {
  const score = report.value?.summary.score
  if (score === null || score === undefined) return 'score-unanswered'
  if (score >= 80) return 'score-high'
  if (score >= 50) return 'score-sustainable'
  return 'score-basic'
})
const consideredRequirements = computed(() => {
  return Number(report.value?.summary.considered ?? report.value?.summary.evaluated ?? 0)
})
const coveragePercentage = computed(() => {
  const total = report.value?.summary.total || 0
  return total ? Math.round(consideredRequirements.value / total * 100) : 0
})
const previewDraftMessage = computed(() => {
  const summary = report.value?.summary || {}
  const drafts = []
  const cscDrafts = Number(summary.drafts || 0)
  const directorDrafts = Number(summary.directorDrafts || 0)
  if (cscDrafts) drafts.push(`${cscDrafts} ${pluralize(cscDrafts, 'resposta do CSC', 'respostas do CSC')}`)
  if (directorDrafts) drafts.push(`${directorDrafts} ${pluralize(directorDrafts, 'resposta do Diretor', 'respostas do Diretor')}`)

  return `Esta prévia inclui ${drafts.join(' e ')} em rascunho. Nota, distribuição, divergências e ranking podem mudar após o envio.`
})
const rankingLabel = computed(() => {
  const position = report.value?.summary.rankingPosition
  return position ? position + 'º lugar' : '-'
})
const areaOptions = computed(() => uniqueOptions((report.value?.details || []).map(item => ({ id: item.areaId, name: item.area }))))
const mandalaAreaOptions = computed(() => uniqueOptions((report.value?.details || []).map(item => ({ id: item.mandalaAreaId, name: item.mandalaArea }))))
const filteredBaseDetails = computed(() => (report.value?.details || []).filter(item => {
  if (selectedArea.value && String(item.areaId) !== selectedArea.value) return false
  const mandalaKey = String(item.mandalaAreaId || item.mandalaArea)
  if (selectedMandalaArea.value && mandalaKey !== selectedMandalaArea.value) return false
  const itemLayer = item.cscLayer || 'unanswered'
  return !selectedLayer.value || itemLayer === selectedLayer.value
}))
const distributionTotal = computed(() => {
  return filteredBaseDetails.value.filter(item => Boolean(item.cscLayer)).length
})
const distributionChartRows = computed(() => chartLayers.map(layer => {
  const count = filteredBaseDetails.value.filter(item => item.cscLayer === layer.value).length
  return {
    ...layer,
    count,
    percentage: distributionTotal.value ? Math.round(count / distributionTotal.value * 100) : 0
  }
}))
const distributionChartData = computed(() => ({
  labels: distributionChartRows.value.map(row => row.label),
  datasets: [{
    label: 'Respostas',
    data: distributionChartRows.value.map(row => row.percentage),
    backgroundColor: distributionChartRows.value.map(row => row.color),
    barPercentage: .72,
    borderRadius: 4,
    borderSkipped: false,
    categoryPercentage: .72,
    maxBarThickness: 44
  }]
}))
const distributionChartDescription = computed(() => {
  const values = distributionChartRows.value.map(row => {
    const answerLabel = row.count === 1 ? 'resposta' : 'respostas'
    return `${row.label}: ${row.percentage}% (${row.count} ${answerLabel})`
  })
  return `Distribuição percentual por camada. ${values.join('. ')}`
})
const distributionChartOptions = {
  animation: false,
  interaction: {
    intersect: false,
    mode: 'index'
  },
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      callbacks: {
        label: context => {
          const row = distributionChartRows.value[context.dataIndex]
          const answerLabel = row.count === 1 ? 'resposta' : 'respostas'
          return `${row.percentage}% · ${row.count} ${answerLabel}`
        }
      }
    }
  },
  responsive: true,
  scales: performanceChartScales()
}
const mandalaAreaRows = computed(() => {
  const groups = new Map()
  filteredBaseDetails.value.filter(item => Boolean(item.cscLayer)).forEach(item => {
    const key = String(item.mandalaAreaId || item.mandalaArea)
    const group = groups.get(key) || { id: item.mandalaAreaId, name: item.mandalaArea, items: [] }
    group.items.push(item)
    groups.set(key, group)
  })

  return Array.from(groups.values()).map(group => ({
    id: group.id,
    name: group.name,
    distributionRows: chartLayers.map(layer => {
      const count = group.items.filter(item => item.cscLayer === layer.value).length
      return { layer: layer.value, count, percentage: group.items.length ? Math.round(count / group.items.length * 100) : 0 }
    })
  }))
})
const mandalaAreaLabel = computed(() => {
  const total = mandalaAreaRows.value.length
  return total === 1 ? '1 área avaliada' : total + ' áreas avaliadas'
})
const mandalaLayerTotals = computed(() => Object.fromEntries(chartLayers.map(layer => [
  layer.value,
  mandalaAreaRows.value.reduce((total, area) => {
    return total + Number(area.distributionRows?.find(row => row.layer === layer.value)?.count || 0)
  }, 0)
])))
const mandalaChartLayers = computed(() => {
  const layersWithAnswers = chartLayers.filter(layer => mandalaLayerTotals.value[layer.value] > 0)
  return layersWithAnswers.length ? layersWithAnswers : chartLayers
})
const mandalaChartData = computed(() => ({
  labels: mandalaAreaRows.value.map(row => wrapChartLabel(row.name)),
  datasets: mandalaChartLayers.value.map(layer => ({
    label: layer.label,
    data: mandalaAreaRows.value.map(row => distributionPercentage(row, layer.value)),
    backgroundColor: layer.color,
    barPercentage: .9,
    borderRadius: 4,
    borderSkipped: false,
    categoryPercentage: .76,
    maxBarThickness: 32
  }))
}))
const mandalaChartDescription = computed(() => {
  const areas = mandalaAreaRows.value.map(area => {
    const values = mandalaChartLayers.value
      .map(layer => `${layer.label}: ${distributionPercentage(area, layer.value)}%`)
      .join(', ')
    return `${area.name}: ${values}`
  })

  return `Distribuição percentual das respostas por área da mandala. ${areas.join('. ')}`
})
const mandalaChartOptions = {
  animation: false,
  interaction: {
    intersect: false,
    mode: 'index'
  },
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      callbacks: {
        title: items => mandalaAreaRows.value[items[0]?.dataIndex]?.name || '',
        label: context => `${context.dataset.label}: ${context.parsed.y}%`
      }
    }
  },
  responsive: true,
  scales: performanceChartScales()
}
function performanceChartScales () {
  return {
    x: {
      grid: { display: false },
      ticks: {
        color: '#6d737b',
        font: { size: 10 },
        maxRotation: 0,
        minRotation: 0
      }
    },
    y: {
      beginAtZero: true,
      max: 100,
      ticks: {
        color: '#6d737b',
        callback: value => value + '%',
        font: { size: 10 },
        stepSize: 10
      },
      grid: { color: 'rgba(0, 0, 0, .07)' }
    }
  }
}
const divergenceLabel = computed(() => {
  const total = filteredDivergences.value.length
  return total === 1 ? '1 requisito divergente' : total + ' requisitos divergentes'
})
const filteredDivergences = computed(() => {
  const visibleIds = new Set(filteredBaseDetails.value.map(item => item.id))
  const term = normalizeSearch(search.value)

  return (report.value?.divergences || []).filter(item => {
    if ((item.difference || 0) < 2 || !visibleIds.has(item.id)) return false
    if (!term) return true
    return [item.name, item.code, item.area].some(value => normalizeSearch(value).includes(term))
  })
})
const filteredRequirements = computed(() => {
  const term = normalizeSearch(search.value)

  return filteredBaseDetails.value.filter(item => {
    if (!term) return true

    return [item.name, item.code, item.area]
      .some(value => normalizeSearch(value).includes(term))
  })
})

onMounted(loadReport)
watch([() => route.params.unitId, () => route.query.cycleId], loadReport)

async function loadReport () {
  loading.value = true
  errorMessage.value = ''

  try {
    const requestedCycleId = route.query.cycleId ? Number(route.query.cycleId) : undefined
    if (reportIdentifier.value && !isSharedReport.value && !isAdministrativePreview.value) {
      throw new Error('Identificador do relatório inválido.')
    }

    const response = isSharedReport.value
      ? await ImportedModulesService.getSharedSguReport(reportIdentifier.value)
      : await ImportedModulesService.getSguReport(
        requestedCycleId,
        isAdministrativePreview.value ? Number(reportIdentifier.value) : undefined
      )
    const { data } = response
    report.value = data
    cycleId.value = data.cycle.id
  } catch (error) {
    if (error.response?.status === 403) {
      errorMessage.value = error.response?.data?.message || 'Este relatório ainda não está disponível para o seu acesso.'
    } else {
      errorMessage.value = firstValidationError(error) || error.response?.data?.message || error.message || 'Não foi possível carregar o relatório do SGU.'
    }
  } finally {
    loading.value = false
  }
}

function firstValidationError (error) {
  const errors = error.response?.data?.errors
  return errors ? Object.values(errors).flat()[0] : ''
}

function layerClass (layer) {
  if (layer === 'high_performance') return 'layer-high'
  if (layer === 'sustainable') return 'layer-sustainable'
  if (layer === 'basic') return 'layer-basic'
  if (layer === 'not_met') return 'layer-not-met'
  if (layer === 'not_applicable') return 'layer-not-applicable'
  return 'layer-unanswered'
}

function layerCount (layer) {
  const details = (report.value?.details || []).filter(item => {
    if (selectedArea.value && String(item.areaId) !== selectedArea.value) return false
    const mandalaKey = String(item.mandalaAreaId || item.mandalaArea)
    return !selectedMandalaArea.value || mandalaKey === selectedMandalaArea.value
  })
  if (!layer) return details.length
  return details.filter(item => (item.cscLayer || 'unanswered') === layer).length
}

function scoreWidth (layer) {
  const score = { not_applicable: 0, not_met: 0, basic: 33, sustainable: 67, high_performance: 100 }[layer]
  return (score || 0) + '%'
}

function scoreLabel (layer) {
  if (layer === 'not_applicable') return 'Não pontua'
  const score = { not_met: 0, basic: 33, sustainable: 67, high_performance: 100 }[layer]
  return score === undefined ? '-' : score + '%'
}

function differenceLabel (difference) {
  return difference === 1 ? '1 nível de diferença' : difference + ' níveis de diferença'
}

function draftSourceLabel (item) {
  const sources = []
  if (item.cscStatus === 'draft') sources.push('CSC')
  if (item.directorStatus === 'draft') sources.push('Diretor')
  return `Rascunho: ${sources.join(' e ')}`
}

function pluralize (amount, singular, plural) {
  return amount === 1 ? singular : plural
}

function formatPercentage (value) {
  return value === null || value === undefined ? '-' : value + '%'
}

function formatWeight (value) {
  return Number(value || 1).toLocaleString('pt-BR', { maximumFractionDigits: 2 })
}

function distributionPercentage (area, layer) {
  return area.distributionRows?.find(row => row.layer === layer)?.percentage || 0
}

function wrapChartLabel (value) {
  const normalized = normalizeSearch(value)
  const shortLabels = [
    ['desempenho', 'Desempenho'],
    ['captacao', 'Captação'],
    ['administrativo', 'Admin.'],
    ['pessoas', 'Pessoas'],
    ['relacionamento', 'Relacionamento']
  ]
  const shortLabel = shortLabels.find(([term]) => normalized.includes(term))
  if (shortLabel) return shortLabel[1]

  const words = String(value || '').split(/\s+/).filter(Boolean)
  if (words.length < 3) return words.join(' ')

  const midpoint = Math.ceil(words.length / 2)
  return [words.slice(0, midpoint).join(' '), words.slice(midpoint).join(' ')]
}

function normalizeSearch (value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('pt-BR')
}

function safeEvidenceUrl (value) {
  try {
    const url = new URL(String(value || ''))
    return ['http:', 'https:'].includes(url.protocol) ? url.toString() : ''
  } catch (_) {
    return ''
  }
}

function isUuid (value) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)
}

function clearFilters () {
  selectedLayer.value = ''
  selectedArea.value = ''
  selectedMandalaArea.value = ''
  search.value = ''
}

function uniqueOptions (items) {
  const seen = new Set()
  return items.filter(item => {
    const key = String(item.id || item.name)
    if (!item.name || seen.has(key)) return false
    seen.add(key)
    return true
  }).sort((left, right) => left.name.localeCompare(right.name, 'pt-BR'))
}

function goBack () {
  router.back()
}

function changeCycle () {
  if (isSharedReport.value) return
  if (!cycleId.value || String(route.query.cycleId || '') === String(cycleId.value)) return

  router.replace({
    query: { ...route.query, cycleId: cycleId.value },
    hash: ''
  })
}

async function printReport () {
  if (!report.value || printLoading.value) return

  printLoading.value = true
  printError.value = ''
  try {
    await nextTick()
    window.print()
  } catch (_) {
    printError.value = 'Não foi possível abrir a impressão do relatório.'
  } finally {
    printLoading.value = false
  }
}

</script>

<style scoped lang="scss">
.sgu-report-page {
  --report-surface: #{$branco};
  --report-background: #{$cinza-claro};
  --report-border: #{$cinza};
  --report-text: #{$preto};
  --report-muted: #6d737b;
  --report-primary: #{$vermelho};
  --layer-not-met: #a92d3b;
  --layer-not-met-soft: #fdecee;
  --layer-basic: #9b6300;
  --layer-basic-soft: #fff3d8;
  --layer-sustainable: #246a3a;
  --layer-sustainable-soft: #e8f5ec;
  --layer-high: #245b92;
  --layer-high-soft: #e9f1fa;
  --layer-unanswered: #6f7379;
  --layer-unanswered-soft: #f0f1f3;

  background: var(--report-background);
  color: var(--report-text);
  min-height: 100vh;
  padding: 30px;
}

.report-header,
.preview-notice,
.summary-section,
.content-panel,
.distribution-grid,
.report-skeleton,
.state-panel {
  margin-left: auto;
  margin-right: auto;
  max-width: 1280px;
}

.report-header {
  background: #c0001d;
  border-radius: 10px;
  color: $branco;
  padding: 20px 24px;
}

.report-heading,
.section-heading {
  align-items: flex-end;
  display: flex;
  gap: 20px;
  justify-content: space-between;
}

.report-heading {
  align-items: center;
}

.report-title {
  flex: 1 1 auto;
  min-width: 240px;
}

.report-header-actions {
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  flex-wrap: nowrap;
  gap: 10px;
  justify-content: flex-end;
}

.header-eyebrow {
  color: #ffd0d7;
  display: block;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: .03em;
  margin-bottom: 5px;
}

.header-back-action {
  align-items: center;
  background: transparent;
  border: 0;
  color: #ffd0d7;
  cursor: pointer;
  display: inline-flex;
  font: inherit;
  font-size: 12px;
  margin: -4px 0 4px;
  padding: 4px 0;
  text-align: left;
}

.header-back-action:hover {
  color: $branco;
  text-decoration: underline;
}

.header-back-action:focus-visible {
  outline-color: $branco;
}

.semester-selector {
  align-items: center;
  display: flex;
  gap: 8px;
  margin: 0;
}

.semester-selector span {
  color: #ffe7ea;
  font-size: 11px;
  font-weight: 700;
}

.semester-selector select,
.header-action {
  border-radius: 8px;
  box-sizing: border-box;
  font: inherit;
  font-size: 12px;
  font-weight: 700;
  height: 44px;
  min-height: 44px;
}

.semester-selector select {
  background: $branco;
  border: 1px solid rgba(255, 255, 255, .7);
  color: $preto;
  min-width: 130px;
  padding: 0 34px;
  text-align: center;
  text-align-last: center;
}

.header-action {
  align-items: center;
  cursor: pointer;
  display: inline-flex;
  justify-content: center;
  padding: 0 14px;
  text-decoration: none;
}

.header-action.primary {
  background: $branco;
  border: 1px solid $branco;
  color: $vinho;
}

.header-action:disabled,
.semester-selector select:disabled {
  cursor: wait;
  opacity: .65;
}

.header-error {
  background: rgba(255, 255, 255, .95);
  border-radius: 8px;
  color: $vinho;
  font-size: 12px;
  margin: 15px 0 0;
  padding: 10px 12px;
}

.preview-notice {
  background: var(--layer-basic-soft);
  border-left: 4px solid var(--layer-basic);
  border-radius: 16px;
  color: var(--report-text);
  display: grid;
  gap: 5px;
  margin-top: 25px;
  padding: 15px 20px;
}

.preview-notice strong {
  color: var(--layer-basic);
}

.preview-notice p {
  margin: 0;
}

.eyebrow {
  color: $vinho;
  display: block;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: .03em;
  margin-bottom: 5px;
}

h1,
h2,
p {
  margin-top: 0;
}

h1 {
  font-size: 24px;
  line-height: 32px;
  margin-bottom: 5px;
}

h2 {
  font-size: 20px;
  line-height: 28px;
  margin-bottom: 0;
}

.report-heading p,
.section-heading small {
  color: var(--report-muted);
  margin-bottom: 0;
}

.report-heading p {
  color: #ffe7ea;
}

.status-badge {
  align-items: center;
  background: var(--layer-basic-soft);
  border: 0;
  border-radius: 999px;
  box-sizing: border-box;
  color: var(--layer-basic);
  display: inline-flex;
  font-size: 12px;
  font-weight: 700;
  height: 44px;
  min-height: 44px;
  padding: 11px 12px;
}

.status-badge.complete {
  background: var(--layer-sustainable-soft);
  border-color: var(--layer-sustainable);
  color: var(--layer-sustainable);
}

.layer-filters span {
  background: var(--report-background);
  border-radius: 999px;
  color: var(--report-muted);
  font-size: 11px;
  padding: 3px 7px;
}

.summary-section,
.content-panel {
  scroll-margin-top: 80px;
}

.report-filter-panel { padding-bottom: 20px; }
.report-filter-grid { display: grid; gap: 14px; grid-template-columns: repeat(3, minmax(0, 1fr)); margin-top: 18px; }
.report-filter-grid label { display: grid; gap: 6px; }
.report-filter-grid label span { color: var(--report-muted); font-size: 11px; font-weight: 700; text-transform: uppercase; }
.report-filter-grid select { background: $branco; border: 1px solid $cinza-medio; border-radius: 10px; color: var(--report-text); font: inherit; min-height: 44px; padding: 0 12px; }
.clear-filter-action { background: transparent; border: 0; color: $vinho; cursor: pointer; font: inherit; font-size: 12px; font-weight: 700; }

.summary-section {
  margin-top: 30px;
}

.summary-grid {
  display: grid;
  gap: 15px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin-top: 15px;
}

.summary-grid article {
  background: var(--report-surface);
  border: 1px solid var(--report-border);
  border-radius: 16px;
  display: grid;
  gap: 6px;
  min-height: 145px;
  padding: 20px;
}

.summary-grid .primary-summary {
  border-top: 4px solid var(--report-primary);
}

.summary-grid .primary-summary.score-basic {
  border-top-color: var(--layer-basic);
}

.summary-grid .primary-summary.score-basic > strong {
  color: var(--layer-basic);
}

.summary-grid .primary-summary.score-sustainable {
  border-top-color: var(--layer-sustainable);
}

.summary-grid .primary-summary.score-sustainable > strong {
  color: var(--layer-sustainable);
}

.summary-grid .primary-summary.score-high {
  border-top-color: var(--layer-high);
}

.summary-grid .primary-summary.score-high > strong {
  color: var(--layer-high);
}

.summary-grid .primary-summary.score-unanswered {
  border-top-color: var(--layer-unanswered);
}

.summary-grid .primary-summary.score-unanswered > strong {
  color: var(--layer-unanswered);
}

.summary-grid span,
.answer-information span {
  color: var(--report-muted);
  font-size: 12px;
  font-weight: 600;
}

.summary-grid strong {
  align-self: end;
  font-size: 28px;
  line-height: 34px;
}

.summary-grid small {
  color: var(--report-muted);
}

.content-panel {
  background: var(--report-surface);
  border: 1px solid var(--report-border);
  border-radius: 16px;
  margin-top: 25px;
  padding: 25px;
}

.distribution-grid {
  display: grid;
  gap: 25px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin-top: 25px;
}

.distribution-grid > .content-panel {
  margin: 0;
  max-width: none;
  min-width: 0;
}

.distribution-card .section-heading {
  align-items: flex-start;
}

.distribution-card h2 {
  font-size: 18px;
  line-height: 25px;
}

.calculation-toggle {
  background: $branco;
  border: 1px solid $cinza-medio;
  border-radius: 10px;
  color: $vinho;
  cursor: pointer;
  font: inherit;
  font-size: 12px;
  font-weight: 700;
  min-height: 44px;
  padding: 0 14px;
}

.calculation-content {
  border-top: 1px solid var(--report-border);
  margin-top: 25px;
  padding-top: 20px;
}

.calculation-content > p {
  color: var(--report-muted);
  line-height: 1.6;
  margin-bottom: 20px;
}

.calculation-grid {
  display: grid;
  gap: 20px;
  grid-template-columns: minmax(0, 1.4fr) minmax(260px, .8fr);
}

.score-scale {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(5, minmax(0, 1fr));
}

.score-scale > span {
  border: 1px solid var(--report-border);
  border-radius: 10px;
  display: grid;
  gap: 5px;
  padding: 12px;
}

.score-scale i {
  border-radius: 999px;
  height: 8px;
  margin-bottom: 3px;
  width: 100%;
}

.score-scale strong,
.score-scale b {
  font-size: 11px;
}

.score-scale b {
  color: var(--report-muted);
}

.classification-box {
  background: $cinza-claro;
  border-radius: 10px;
  display: grid;
  gap: 10px;
  padding: 15px;
}

.classification-box > p {
  color: var(--report-text);
  font-size: 12px;
  font-weight: 700;
  margin: 0;
}

.classification-box ul {
  display: grid;
  gap: 8px;
  list-style: none;
  margin: 0;
  padding: 0;
}

.classification-box li {
  align-items: center;
  border-top: 1px solid var(--report-border);
  display: flex;
  gap: 15px;
  justify-content: space-between;
  padding-top: 8px;
}

.classification-box li > span {
  color: var(--report-muted);
  font-size: 12px;
}

.classification-box strong {
  font-size: 12px;
  text-align: right;
}

.weight-explanation {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  list-style: none;
  margin: 20px 0 0;
  padding: 0;
}

.weight-explanation li {
  background: $cinza-claro;
  border-radius: 10px;
  display: grid;
  gap: 4px;
  padding: 12px;
}

.weight-explanation span {
  color: var(--report-muted);
  font-size: 12px;
  line-height: 1.5;
}

.layer-filters i {
  border-radius: 50%;
  display: block;
  height: 10px;
  width: 10px;
}

.performance-chart-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 14px;
  margin-top: 18px;
}

.performance-chart-legend span {
  align-items: center;
  color: var(--report-muted);
  display: inline-flex;
  font-size: 11px;
  font-weight: 600;
  gap: 5px;
}

.performance-chart-legend i {
  border-radius: 2px;
  display: block;
  height: 10px;
  width: 10px;
}

.performance-chart {
  height: 250px;
  margin-top: 12px;
  min-width: 0;
  position: relative;
}

.empty-state.compact {
  padding-bottom: 10px;
}

.divergence-list {
  display: grid;
  gap: 10px;
  list-style: none;
  margin: 20px 0 0;
  padding: 0;
}

.divergence-list li {
  border: 1px solid var(--report-border);
  border-radius: 12px;
  overflow: hidden;
}

.divergence-list details > summary {
  align-items: center;
  cursor: pointer;
  display: grid;
  gap: 20px;
  grid-template-columns: minmax(220px, 1fr) auto 145px;
  list-style: none;
  padding: 15px;
}

.divergence-list details > summary::-webkit-details-marker { display: none; }
.divergence-list details > summary:hover { background: $cinza-claro; }
.divergence-details { border-top: 1px solid var(--report-border); padding: 18px; }
.divergence-details .criterion-grid { margin-top: 0; }

.divergence-copy {
  display: grid;
  gap: 4px;
}

.divergence-copy small {
  color: var(--report-muted);
}

.draft-marker {
  background: var(--layer-basic-soft);
  border-radius: 999px;
  color: var(--layer-basic);
  font-size: 11px;
  font-weight: 700;
  padding: 4px 8px;
  width: max-content;
}

.comparison {
  align-items: center;
  display: flex;
  gap: 10px;
}

.comparison > span:not(.comparison-arrow) {
  display: grid;
  gap: 5px;
}

.comparison small {
  color: var(--report-muted);
  font-size: 11px;
}

.comparison-arrow {
  color: var(--report-muted);
  margin-top: 18px;
}

.difference {
  color: var(--report-muted);
  font-size: 12px;
  text-align: right;
}

.layer-badge {
  align-items: center;
  border: 0;
  border-radius: 999px;
  display: inline-flex;
  font-size: 11px;
  font-weight: 700;
  justify-content: center;
  min-height: 28px;
  padding: 5px 9px;
  text-align: center;
  width: 132px;
}

.requirement-filters {
  align-items: end;
  display: grid;
  gap: 20px;
  grid-template-columns: minmax(220px, .7fr) minmax(0, 1.8fr);
  margin-top: 20px;
}

.search-field,
.requirement-filters fieldset {
  display: grid;
  gap: 7px;
}

.search-field span,
.requirement-filters legend {
  color: var(--report-text);
  font-size: 12px;
  font-weight: 700;
}

.search-field input {
  background: $branco;
  border: 1px solid $cinza-medio;
  border-radius: 14px;
  color: var(--report-text);
  font: inherit;
  min-height: 44px;
  padding: 0 14px;
}

.requirement-filters fieldset {
  border: 0;
  margin: 0;
  min-width: 0;
  padding: 0;
}

.layer-filters {
  display: flex;
  gap: 7px;
  overflow-x: auto;
  padding: 1px;
}

.layer-filters button {
  align-items: center;
  background: var(--report-surface);
  border: 1px solid var(--report-border);
  border-radius: 999px;
  color: var(--report-text);
  cursor: pointer;
  display: flex;
  flex: 0 0 auto;
  font: inherit;
  font-size: 12px;
  gap: 7px;
  min-height: 44px;
  padding: 0 12px;
}

.layer-filters button.active {
  background: $cinza-claro;
  border-color: $vinho;
  color: $vinho;
  font-weight: 700;
}

.requirements-list {
  border-top: 1px solid var(--report-border);
  display: grid;
  margin-top: 20px;
}

.requirements-list details {
  border-bottom: 1px solid var(--report-border);
}

.requirements-list summary {
  align-items: center;
  cursor: pointer;
  display: grid;
  gap: 20px;
  grid-template-columns: minmax(0, 1fr) auto;
  list-style-position: outside;
  min-height: 84px;
  padding: 15px 10px;
}

.requirements-list summary:hover {
  background: $cinza-claro;
}

.requirement-title {
  display: grid;
  gap: 4px;
}

.requirement-title small,
.weight {
  color: var(--report-muted);
  font-size: 11px;
}

.requirement-result {
  align-items: center;
  display: grid;
  gap: 10px;
  grid-template-columns: auto auto 48px;
}

.requirement-result > strong {
  text-align: right;
}

.requirement-details {
  background: $cinza-claro;
  border-radius: 12px;
  margin: 0 10px 15px;
  padding: 20px;
}

.performance-track {
  background: var(--layer-unanswered-soft);
  border-radius: 999px;
  height: 8px;
  overflow: hidden;
}

.performance-track span {
  display: block;
  height: 100%;
}

.criterion-grid {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin-top: 20px;
}

.criterion-grid article {
  background: var(--report-surface);
  border: 1px solid var(--report-border);
  border-radius: 10px;
  padding: 15px;
}

.criterion-grid article.selected-basic {
  border-color: var(--layer-basic);
  box-shadow: inset 0 3px 0 var(--layer-basic);
}

.criterion-grid article.selected-sustainable {
  border-color: var(--layer-sustainable);
  box-shadow: inset 0 3px 0 var(--layer-sustainable);
}

.criterion-grid article.selected-high {
  border-color: var(--layer-high);
  box-shadow: inset 0 3px 0 var(--layer-high);
}

.criterion-label {
  font-size: 12px;
  font-weight: 700;
}

.criterion-grid p,
.answer-information p {
  color: var(--report-text);
  font-size: 12px;
  line-height: 1.6;
  margin: 8px 0 0;
}

.answer-information {
  border-top: 1px solid var(--report-border);
  display: grid;
  gap: 15px;
  margin-top: 20px;
  padding-top: 20px;
}

.answer-information > div {
  display: grid;
  gap: 6px;
}

.answer-information a {
  align-items: center;
  color: $vinho;
  display: inline-flex;
  font-size: 12px;
  font-weight: 700;
  gap: 6px;
  min-height: 28px;
  text-decoration: none;
  width: max-content;
}

.answer-information a:hover {
  text-decoration: underline;
}

.evidence-information {
  align-content: start;
}

.evidence-information b {
  font-size: 16px;
}

.empty-state,
.state-panel {
  align-items: center;
  display: flex;
  flex-direction: column;
  padding: 40px 20px;
  text-align: center;
}

.empty-state p,
.state-panel p {
  color: var(--report-muted);
  margin: 7px 0 15px;
}

.empty-state button,
.state-panel button {
  background: $vermelho;
  border: 0;
  border-radius: 14px;
  color: $branco;
  cursor: pointer;
  font: inherit;
  font-weight: 700;
  min-height: 44px;
  padding: 0 18px;
}

.state-panel {
  background: var(--report-surface);
  border: 1px solid var(--report-border);
  border-radius: 16px;
  margin-top: 25px;
}

.state-panel.error {
  border-color: $roxo;
}

.report-skeleton {
  background: var(--report-surface);
  border: 1px solid var(--report-border);
  border-radius: 16px;
  margin-top: 25px;
  padding: 25px;
}

.report-skeleton > span {
  color: var(--report-muted);
  display: block;
  margin-bottom: 20px;
}

.skeleton-heading,
.skeleton-grid i,
.skeleton-content {
  animation: skeleton-pulse 1.3s ease-in-out infinite alternate;
  background: $cinza;
  border-radius: 10px;
}

.skeleton-heading {
  height: 28px;
  width: min(320px, 70%);
}

.skeleton-grid {
  display: grid;
  gap: 15px;
  grid-template-columns: repeat(4, 1fr);
  margin-top: 20px;
}

.skeleton-grid i {
  height: 120px;
}

.skeleton-content {
  height: 240px;
  margin-top: 20px;
}

.layer-not-met {
  background-color: var(--layer-not-met-soft);
  color: var(--layer-not-met);
}

.layer-not-applicable { background-color: #eceef1; color: #616771; }
.score-scale i.layer-not-applicable,
.layer-filters i.layer-not-applicable,
.performance-track .layer-not-applicable { background-color: #7b8088; }

.score-scale i.layer-not-met,
.layer-filters i.layer-not-met,
.performance-track .layer-not-met {
  background-color: var(--layer-not-met);
}

.layer-basic {
  background-color: var(--layer-basic-soft);
  color: var(--layer-basic);
}

.score-scale i.layer-basic,
.layer-filters i.layer-basic,
.performance-track .layer-basic {
  background-color: var(--layer-basic);
}

.layer-sustainable {
  background-color: var(--layer-sustainable-soft);
  color: var(--layer-sustainable);
}

.score-scale i.layer-sustainable,
.layer-filters i.layer-sustainable,
.performance-track .layer-sustainable {
  background-color: var(--layer-sustainable);
}

.layer-high {
  background-color: var(--layer-high-soft);
  color: var(--layer-high);
}

.score-scale i.layer-high,
.layer-filters i.layer-high,
.performance-track .layer-high {
  background-color: var(--layer-high);
}

.layer-unanswered {
  background-color: var(--layer-unanswered-soft);
  color: var(--layer-unanswered);
}

*:focus-visible {
  outline: 3px solid $vermelho;
  outline-offset: 2px;
}

@keyframes skeleton-pulse {
  from { opacity: .55; }
  to { opacity: 1; }
}

@media (prefers-reduced-motion: reduce) {
  .skeleton-heading,
  .skeleton-grid i,
  .skeleton-content {
    animation: none;
  }
}

@media (max-width: 980px) {
  .report-heading {
    align-items: flex-start;
    flex-direction: column;
  }

  .report-header-actions {
    justify-content: flex-start;
    width: 100%;
  }

  .distribution-grid {
    grid-template-columns: 1fr;
  }

  .summary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .calculation-grid {
    grid-template-columns: 1fr;
  }

  .divergence-list details > summary {
    align-items: start;
    grid-template-columns: 1fr;
  }

  .difference {
    text-align: left;
  }

  .requirement-filters,
  .criterion-grid,
  .report-filter-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 620px) {
  .sgu-report-page {
    padding: 15px;
  }

  .report-header,
  .preview-notice,
  .content-panel {
    padding: 20px;
  }

  .report-heading,
  .section-heading {
    align-items: flex-start;
    flex-direction: column;
    gap: 10px;
  }

  .report-header-actions {
    display: grid;
    grid-template-columns: 1fr;
    justify-items: stretch;
    width: 100%;
  }

  .semester-selector select,
  .header-action {
    width: 100%;
  }

  .status-badge {
    justify-self: start;
  }

  .summary-grid,
  .skeleton-grid {
    grid-template-columns: 1fr;
  }

  .score-scale {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .weight-explanation {
    grid-template-columns: 1fr;
  }

  .summary-grid article {
    min-height: 120px;
  }

  .comparison {
    align-items: flex-start;
    flex-direction: column;
  }

  .comparison-arrow {
    display: none;
  }

  .requirements-list summary {
    align-items: start;
    grid-template-columns: 1fr;
  }

  .requirement-result {
    grid-template-columns: auto auto 1fr;
  }

  .requirement-result > strong {
    text-align: left;
  }
}

@media print {
  .sgu-report-page { max-width: none; padding: 0; }
  .header-back-action,
  .semester-selector,
  .header-action,
  .calculation-toggle,
  .report-filter-panel,
  .requirement-filters,
  .clear-filter-action { display: none !important; }
  .report-header,
  .summary-grid article,
  .content-panel { break-inside: avoid; box-shadow: none; }
  .report-header { margin: 0; }
  .content-panel { margin-top: 14px; }
  .distribution-grid { gap: 14px; margin-top: 14px; }
  .performance-chart { height: 220px; }
  * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
}
</style>
